import { RefObject, useEffect, useRef } from 'react';

interface UseWheelStepOptions {
  step: number;
  min?: number;
  max?: number;
  onStep: (next: number) => void;
}

/**
 * A focused number input changes its value on wheel scroll natively, but that
 * scroll also bubbles into the scrollable inspector panel underneath it.
 * React registers onWheel as a passive root listener, so preventDefault()
 * inside a JSX handler silently no-ops — a native, non-passive listener is
 * the only way to actually cancel the scroll. Cancelling it also cancels the
 * browser's own increment/decrement, so onStep replicates that step.
 */
export function useWheelStep(
  inputRef: RefObject<HTMLInputElement>,
  currentValue: number | undefined,
  { step, min, max, onStep }: UseWheelStepOptions,
): void {
  const valueRef = useRef(currentValue);
  valueRef.current = currentValue;

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return undefined;

    const handleWheel = (e: WheelEvent) => {
      if (document.activeElement !== input) return;
      e.preventDefault();
      let next = (valueRef.current ?? 0) + (e.deltaY < 0 ? step : -step);
      if (min !== undefined) next = Math.max(min, next);
      if (max !== undefined) next = Math.min(max, next);
      onStep(Number(next.toFixed(10)));
    };

    input.addEventListener('wheel', handleWheel, { passive: false });
    return () => input.removeEventListener('wheel', handleWheel);
  }, [inputRef, step, min, max, onStep]);
}
